import React from 'react';
import styled from 'styled-components';
import {
    Builder_,
    Checkbox_,
    Dropdown_,
    Main_Plain
} from 'monica-alexandria';
import { read_public_source_label } from '../../../../utils/publicProductLabel';

const LeadFiltersForm = styled.div`
    display: grid;
    gap: 2rem;
`;

export const defaultLeadFilters = {
    sortBy: 'leadCreatedAt',
    sortOrder: 'newest',
    difCodeBrands: 'all',
    sources: [],
    registrationStatus: '',
    withPhone: false,
    withEmail: false,
    questProviders: []
};

export const leadSortOptions = [
    { value: 'newestLeads', label: 'Newest leads', sortBy: 'leadCreatedAt', sortOrder: 'newest' },
    { value: 'newestBrand', label: 'Newest brand', sortBy: 'leadBrandCreatedAt', sortOrder: 'newest' },
    { value: 'oldestLead', label: 'Oldest lead', sortBy: 'leadCreatedAt', sortOrder: 'oldest' },
    { value: 'oldestBrand', label: 'Oldest brand', sortBy: 'leadBrandCreatedAt', sortOrder: 'oldest' }
];

/**
 * Reads the supported Leads sort option represented by an API filter pair.
 */
export const read_lead_sort_option = (filters = {}) => {
    const sortBy = `${filters?.sortBy || defaultLeadFilters.sortBy}`;
    const sortOrder = `${filters?.sortOrder || defaultLeadFilters.sortOrder}`.toLowerCase();

    return leadSortOptions.find((option) => option.sortBy === sortBy && option.sortOrder === sortOrder)
        || leadSortOptions[0];
};

/**
 * Returns a detached, API-safe Leads filter state.
 */
export const read_lead_filters = (filters = {}) => {
    const sortOption = read_lead_sort_option(filters);
    const difCodeBrands = `${filters?.difCodeBrands || defaultLeadFilters.difCodeBrands}`.toLowerCase();

    return {
        sortBy: sortOption.sortBy,
        sortOrder: sortOption.sortOrder,
        difCodeBrands: ['yes', 'no'].includes(difCodeBrands) ? difCodeBrands : 'all',
        sources: [...new Set((filters?.sources || (filters?.source ? [filters.source] : [])).map((source) => `${source || ''}`.trim()).filter(Boolean))],
        registrationStatus: ['registered', 'unregistered'].includes(`${filters?.registrationStatus || ''}`.toLowerCase())
            ? `${filters.registrationStatus}`.toLowerCase()
            : '',
        withPhone: Boolean(filters?.withPhone),
        withEmail: Boolean(filters?.withEmail),
        questProviders: [...new Set((filters?.questProviders || []).map((provider) => `${provider || ''}`.trim().toLowerCase())
            .filter((provider) => ['meta', 'google'].includes(provider)))]
    };
};

/**
 * Applies one safe Sort by option without exposing arbitrary backend sort fields.
 */
export const read_lead_sort_change = (filters, selectedValue) => {
    const selectedOption = leadSortOptions.find((option) => option.value === selectedValue) || leadSortOptions[0];

    return read_lead_filters({
        ...filters,
        sortBy: selectedOption.sortBy,
        sortOrder: selectedOption.sortOrder
    });
};

/**
 * Normalizes database-backed Source options while preserving their stored values.
 */
export const read_source_options = (sources = []) => {
    const seenValues = new Set();

    return (sources || []).map((source) => {
        const value = `${source?.value || source || ''}`.trim();
        const label = `${source?.label || value}`.trim();

        return { value, label: read_public_source_label(label) };
    }).filter((source) => {
        if (!source.value || seenValues.has(source.value)) {
            return false;
        }

        seenValues.add(source.value);
        return true;
    });
};

/**
 * Reads whether any Leads filter is active.
 */
export const read_filters_active = (filters) => {
    const normalizedFilters = read_lead_filters(filters);

    return Boolean(
        normalizedFilters.sources.length
        || normalizedFilters.registrationStatus
        || normalizedFilters.difCodeBrands !== defaultLeadFilters.difCodeBrands
        || read_lead_sort_option(normalizedFilters).value !== leadSortOptions[0].value
        || normalizedFilters.withPhone
        || normalizedFilters.withEmail
        || normalizedFilters.questProviders.length
    );
};

/**
 * Counts each active selection shown in the Leads filter slider.
 */
export const read_active_filters_count = (filters) => {
    const normalizedFilters = read_lead_filters(filters);

    return [
        ...normalizedFilters.sources.map(() => true),
        Boolean(normalizedFilters.registrationStatus),
        normalizedFilters.difCodeBrands !== defaultLeadFilters.difCodeBrands,
        read_lead_sort_option(normalizedFilters).value !== leadSortOptions[0].value,
        normalizedFilters.withPhone,
        normalizedFilters.withEmail,
        ...normalizedFilters.questProviders.map(() => true)
    ].filter(Boolean).length;
};

/**
 * Adds or removes one checkbox value without mutating the current draft.
 */
export const read_toggled_filter_value = (values, selectedValue) => {
    const currentValues = [...(values || [])];

    if (currentValues.includes(selectedValue)) {
        return currentValues.filter((value) => value !== selectedValue);
    }

    return [...currentValues, selectedValue];
};

/**
 * Displays Leads filters grouped by the underlying Prospect data source.
 */
export const Lead_Filters = (props) => {
    const t = props.t || ((value) => value);
    const filters = read_lead_filters(props.filters);
    const sourceOptions = read_source_options(props.sourceOptions);
    const change_filters = (nextFilters) => props.onChange(read_lead_filters(nextFilters));
    const toggle_registration_status = (registrationStatus) => {
        change_filters({
            ...filters,
            registrationStatus: filters.registrationStatus === registrationStatus ? '' : registrationStatus
        });
    };

    return (
        <LeadFiltersForm>
            <Builder_ title={t('Sort')}>
                <Dropdown_ label={t('Sort by')}>
                    <select
                        aria-label={t('Sort by')}
                        value={read_lead_sort_option(filters).value}
                        onChange={(event) => change_filters(read_lead_sort_change(filters, event.target.value))}
                    >
                        {leadSortOptions.map((option) => (
                            <option key={option.value} value={option.value}>{t(option.label)}</option>
                        ))}
                    </select>
                </Dropdown_>
            </Builder_>
            <Builder_ title={t('Show only')}>
                <Dropdown_ label={t('Leads with DIF-code brands')}>
                    <select
                        aria-label={t('Leads with DIF-code brands')}
                        value={filters.difCodeBrands}
                        onChange={(event) => change_filters({
                            ...filters,
                            difCodeBrands: event.target.value
                        })}
                    >
                        <option value="all">{t('All')}</option>
                        <option value="yes">{t('Yes')}</option>
                        <option value="no">{t('No')}</option>
                    </select>
                </Dropdown_>
            </Builder_>
            <Builder_ title={t('Quests')}>
                <Checkbox_
                    text={t('Meta')}
                    checked={filters.questProviders.includes('meta')}
                    onClick={() => change_filters({
                        ...filters,
                        questProviders: read_toggled_filter_value(filters.questProviders, 'meta')
                    })}
                />
                <Checkbox_
                    text={t('Google')}
                    checked={filters.questProviders.includes('google')}
                    onClick={() => change_filters({
                        ...filters,
                        questProviders: read_toggled_filter_value(filters.questProviders, 'google')
                    })}
                />
            </Builder_>
            <Builder_ title={t('Status')}>
                <Checkbox_
                    text={t('Registered')}
                    checked={filters.registrationStatus === 'registered'}
                    onClick={() => toggle_registration_status('registered')}
                />
                <Checkbox_
                    text={t('Unregistered')}
                    checked={filters.registrationStatus === 'unregistered'}
                    onClick={() => toggle_registration_status('unregistered')}
                />
            </Builder_>
            <Builder_ title={t('Contact information')}>
                <Checkbox_
                    text={t('With phone')}
                    checked={filters.withPhone}
                    onClick={() => change_filters({ ...filters, withPhone: !filters.withPhone })}
                />
                <Checkbox_
                    text={t('With email')}
                    checked={filters.withEmail}
                    onClick={() => change_filters({ ...filters, withEmail: !filters.withEmail })}
                />
            </Builder_>
            <Builder_ title={t('Source')}>
                {sourceOptions.map((sourceOption) => (
                    <Checkbox_
                        key={sourceOption.value}
                        text={sourceOption.label}
                        checked={filters.sources.includes(sourceOption.value)}
                        onClick={() => change_filters({
                            ...filters,
                            sources: read_toggled_filter_value(filters.sources, sourceOption.value)
                        })}
                    />
                ))}
            </Builder_>
            {read_filters_active(props.appliedFilters) || read_filters_active(filters) ? (
                <Main_Plain text={t('Clear filters')} onClick={props.onClear} />
            ) : null}
        </LeadFiltersForm>
    );
};
